import { formatTimeTo12Hour } from "@/lib/utils";
import { timeSpanToMinutes } from "@/lib/utils/duration-format";
import { DayOfWeek } from "@/openapi/generated";
import type { ClassDayResponse } from "@/openapi/generated/models";

/** Monday first, then through the week. Unknown days sort after Sunday. */
const WEEKDAY_ORDER: Record<string, number> = {
  [DayOfWeek.Monday]: 1,
  [DayOfWeek.Tuesday]: 2,
  [DayOfWeek.Wednesday]: 3,
  [DayOfWeek.Thursday]: 4,
  [DayOfWeek.Friday]: 5,
  [DayOfWeek.Saturday]: 6,
  [DayOfWeek.Sunday]: 7,
};

const MISSING_SCHEDULE_KEY = Number.POSITIVE_INFINITY;

export function classDaySortKey(meeting: ClassDayResponse): number {
  const dayOrder = WEEKDAY_ORDER[meeting.day] ?? 99;
  const minutes = timeSpanToMinutes(meeting.startTime) ?? 0;
  return dayOrder * 10_000 + minutes;
}

export function earliestClassMeetingSortKey(
  classDays: ClassDayResponse[] | null | undefined,
): number {
  if (!classDays || classDays.length === 0) return MISSING_SCHEDULE_KEY;

  let best = MISSING_SCHEDULE_KEY;
  for (const meeting of classDays) {
    const key = classDaySortKey(meeting);
    if (key < best) best = key;
  }
  return best;
}

export function sortClassDaysByWeek(
  classDays: ClassDayResponse[],
): ClassDayResponse[] {
  return [...classDays].sort((a, b) => classDaySortKey(a) - classDaySortKey(b));
}

/**
 * One line per class meeting: "Monday: 9:00 AM – 10:00 AM" (same idea as admin classes table).
 * Meetings are ordered Monday through Sunday, then by start time.
 */
export function formatClassDaysTimesLines(
  classDays: ClassDayResponse[] | null | undefined,
): string[] {
  if (!classDays || classDays.length === 0) return ["N/A"];
  return sortClassDaysByWeek(classDays).map((cd) => {
    const start = formatTimeTo12Hour(cd.startTime);
    const end = formatTimeTo12Hour(cd.endTime);
    const day = cd.day ?? "-";
    if (start && end) return `${day}: ${start} – ${end}`;
    if (start) return `${day}: ${start}`;
    return String(day);
  });
}

/**
 * Public class detail schedule block, one line per meeting:
 * "tuesday - 12:30 pm - 1:30 pm"
 */
export function formatClassSchedulePublicDetail(
  classDays: ClassDayResponse[] | null | undefined,
): string | null {
  if (!classDays?.length) return null;

  return sortClassDaysByWeek(classDays)
    .map((cd) => {
      const day = cd.day ?? "-";
      const start = formatTimeTo12Hour(cd.startTime).toLowerCase();
      const end = formatTimeTo12Hour(cd.endTime).toLowerCase();
      return `${day} - ${start} - ${end}`;
    })
    .join("\n");
}

/**
 * Comma-joined single line (e.g. filters / exports).
 */
export function formatClassDaysTimesComma(
  classDays: ClassDayResponse[] | null | undefined,
): string {
  return formatClassDaysTimesLines(classDays).join(", ");
}

function meetingTimeLabel(meeting: ClassDayResponse): string {
  const start = formatTimeTo12Hour(meeting.startTime);
  const end = formatTimeTo12Hour(meeting.endTime);
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  return "Time TBD";
}

/**
 * Compact schedule for homepage class cards: full weekday for a single meeting,
 * short days when the class meets more than once.
 */
export function formatHomeClassCardSchedule(
  classDays: ClassDayResponse[] | null | undefined,
): { dayLabel: string; timeText: string } {
  if (!classDays?.length) {
    return { dayLabel: "Schedule TBD", timeText: "Time TBD" };
  }

  const sorted = sortClassDaysByWeek(classDays);
  const times = sorted.map(meetingTimeLabel);
  const uniqueTimes = [...new Set(times)];

  if (sorted.length === 1) {
    return {
      dayLabel: (sorted[0].day ?? "Schedule TBD").toUpperCase(),
      timeText: times[0],
    };
  }

  return {
    dayLabel: sorted
      .map((meeting) => (meeting.day ?? "").slice(0, 3).toUpperCase())
      .filter(Boolean)
      .join(" · "),
    timeText: uniqueTimes.length === 1 ? uniqueTimes[0] : "Multiple times",
  };
}
